import {
    OperationVariables,
    QueryLazyOptions,
    useMutation,
    useReactiveVar,
} from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { TextField } from '@material-ui/core'
import { stripDoubleSpacesTrimStart } from 'bpm/shared/utils/inputUtils'
import clsx from 'clsx'
import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import {
    AddProductDialogState,
    addProductDialogState,
    setProductDialogState,
} from './addProductFormHandler'
import { useAddProductFormStyles } from './formStyles'

type Props = {
    callAutoSuggest: (
        options?: QueryLazyOptions<OperationVariables> | undefined
    ) => void
    autoSuggestData: any
    disabled: boolean
}

const NewProductTypeSection: FC<Props> = ({
    callAutoSuggest,
    autoSuggestData,
    disabled,
}) => {
    const { productType, errors, type, sectionNames } = useReactiveVar(
        addProductDialogState
    )

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const classes = useAddProductFormStyles()

    const [isSnackbarShown, setIsSnackbarShown] = useState(false)

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (!!isSnackbarShown) {
                setIsSnackbarShown(false)
            }

            let tempState: Partial<AddProductDialogState> = {
                productType: stripDoubleSpacesTrimStart(e.target.value),
            }

            if (!!errors.productType) {
                tempState = {
                    ...tempState,
                    errors: { ...errors, productType: '' },
                }
            }

            setProductDialogState({
                ...tempState,
            })

            callAutoSuggest({
                variables: {
                    input: {
                        listingType: type,
                        familyName: sectionNames,
                        productType: stripDoubleSpacesTrimStart(e.target.value),
                        isProductTypeSearch: true,
                        limit: 1,
                    },
                },
            })
        },
        [
            setProductDialogState,
            type,
            sectionNames,
            callAutoSuggest,
            isSnackbarShown,
            setIsSnackbarShown,
            stripDoubleSpacesTrimStart,
        ]
    )

    useEffect(() => {
        if (
            !!productType &&
            !!autoSuggestData?.productListingDetailsAutoSuggest
                ?.productTypeDetails?.length &&
            !!autoSuggestData?.productListingDetailsAutoSuggest?.productTypeDetails.some(
                (family: Record<string, string>) =>
                    family?.productType?.toLowerCase().trim() ===
                    productType.toLowerCase().trim()
            ) &&
            !isSnackbarShown
        ) {
            setIsSnackbarShown(true)
            setProductDialogState({
                errors: { ...errors, productType: 'Duplicate Product Type' },
            })

            showGlobalSnackbar({
                variables: {
                    message:
                        'Duplicate Product Type. Please enter a different one',
                    variant: 'error',
                    id: 'AddProduct_Duplicate_ProductType',
                },
            })
        }
    }, [
        setProductDialogState,
        errors,
        autoSuggestData,
        productType,
        isSnackbarShown,
        setIsSnackbarShown,
    ])

    return (
        <GridItem
            className={clsx(classes.rowContainer, 'rowContainer')}
            data-testid="newProductType"
            xs={12}
        >
            <TextField
                value={productType}
                className={classes.inputField}
                fullWidth
                disabled={disabled}
                variant="outlined"
                error={!!errors.productType}
                label="Product Type"
                onChange={handleChange}
            />
        </GridItem>
    )
}
export default NewProductTypeSection
